import { Vector3d } from "../math/vector3d";
import { fromDate } from "../math/julian_date";
import type { ICondition } from "./condition_evaluator";
import {
  OrbitAroundBodyCondition,
  FullOrbitCondition,
  CrashCondition,
  FuelExhaustedCondition,
  TimeLimitCondition,
  HeliocentricTransferCondition,
} from "./condition_evaluator";

export enum Difficulty {
  Tutorial = "Tutorial",
  Beginner = "Beginner",
  Intermediate = "Intermediate",
  Advanced = "Advanced",
  Expert = "Expert",
  Insane = "Insane",
}

export enum CruxCategory {
  Tutorial = "Tutorial",
  SingleManeuver = "SingleManeuver",
  MultiManeuver = "MultiManeuver",
  Optimization = "Optimization",
  FreeForm = "FreeForm",
  Lesson = "Lesson",
}

export enum HintLevel {
  Nudge = "Nudge",
  Clarification = "Clarification",
  Guidance = "Guidance",
  Detailed = "Detailed",
}

export interface Hint {
  hintLevel: HintLevel;
  text: string;
  unlockAfterSeconds: number;
}

export interface ScoringConfig {
  referenceOptimalDeltaV: number;
  referenceGoodDeltaV: number;
  referenceAcceptableDeltaV: number;
  referenceOptimalTime: number;
  referenceGoodTime: number;
  deltaVWeight: number;
  timeWeight: number;
  accuracyWeight: number;
}

export interface CruxDefinition {
  id: string;
  title: string;
  description: string;
  difficulty: Difficulty;
  category: CruxCategory;
  prerequisites: string[];

  startTimeJD: number;
  bodies: string[];
  startSOIBody: string;
  startPosition: Vector3d;
  startVelocity: Vector3d;

  spacecraftName: string;
  spacecraftDryMass: number;
  spacecraftFuelMass: number;
  spacecraftISP: number;
  spacecraftMaxThrust: number;

  victoryConditions: ICondition[];
  failureConditions: ICondition[];

  scoring: ScoringConfig;
  hints: Hint[];
}

/** Map frame strings from JSON to SOI body name */
function frameToSOIBody(frame: string | undefined): string {
  if (!frame) return "Earth";
  const f = frame.toLowerCase();
  if (f.includes("earth")) return "Earth";
  if (f.includes("mars")) return "Mars";
  if (f.includes("venus")) return "Venus";
  if (f.includes("jupiter")) return "Jupiter";
  if (f.includes("saturn")) return "Saturn";
  if (f.includes("moon") || f.includes("lunar")) return "Moon";
  if (f.includes("sun") || f.includes("helio")) return "Sun";
  return "Earth";
}

/** Parse ISO date string or JD number to Julian Date */
function parseStartTime(val: unknown): number {
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    // Try as JD number string first
    const n = parseFloat(val);
    if (!isNaN(n) && n > 2_000_000) return n;
    // Parse as ISO date
    const d = new Date(val);
    if (!isNaN(d.getTime())) return fromDate(d);
  }
  return 2_460_676.5; // 2025-01-01 default
}

/** Planets that orbit the Sun — orbit_around_body for these from a heliocentric
 *  start means the player needs to match the planet's solar orbit. */
const HELIOCENTRIC_BODIES = new Set([
  "Mercury", "Venus", "Earth", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune",
])

/** Parse condition objects from JSON arrays.
 *  startSOIBody is used to auto-generate heliocentric transfer conditions
 *  when an orbit_around_body targets a different planet from a Sun-SOI start. */
function parseConditions(arr: any[], isVictory: boolean, startSOIBody: string): ICondition[] {
  const conditions: ICondition[] = []
  for (const c of arr) {
    const id = c.id ?? `${c.type}_${conditions.length}`
    switch (c.type) {
      case "orbit_around_body": {
        const body = c.body ?? c.target_body
        conditions.push(new OrbitAroundBodyCondition(id, body, {
          minPeriapsis: c.periapsis_min_km ?? c.min_periapsis ?? 0,
          maxPeriapsis: c.periapsis_max_km ?? c.max_periapsis ?? Infinity,
          minApoapsis: c.apoapsis_min_km ?? c.min_apoapsis ?? 0,
          maxApoapsis: c.apoapsis_max_km ?? c.max_apoapsis ?? Infinity,
          maxEccentricity: c.max_eccentricity ?? c.eccentricity_max,
          maxInclination: c.max_inclination ?? c.inclination_max,
        }))
        break
      }
      case "heliocentric_transfer":
        conditions.push(new HeliocentricTransferCondition(
          id, c.body ?? c.target_body,
          c.sma_tolerance ?? 0.05,
          c.max_eccentricity ?? 0.15,
        ))
        break
      case "full_orbit":
        conditions.push(new FullOrbitCondition(id, c.body ?? c.target_body))
        break
      case "crash":
      case "impact_with_body":
        conditions.push(new CrashCondition(id))
        break
      case "fuel_exhausted":
        conditions.push(new FuelExhaustedCondition(id))
        break
      case "time_limit":
        // Disabled: time limits conflict with launch window planning
        break
      default:
        // Unknown types (delta_v_budget etc.) silently skipped for now
        break
    }
  }
  return conditions
}

/**
 * Parse a crux definition from the JSON stored in Rails definition_json.
 *
 * Handles the actual format from the legacy crux files:
 *   initial_conditions.spacecraft.state.position/velocity
 *   initial_conditions.start_time (ISO string)
 */
export function cruxDefinitionFromJson(json: Record<string, unknown>): CruxDefinition {
  const j = json as Record<string, any>;

  const ic = j.initial_conditions ?? {};
  const sc = ic.spacecraft ?? {};
  const state = sc.state ?? {};
  const pos = state.position ?? {};
  const vel = state.velocity ?? {};
  const scoring = j.scoring ?? {};
  const refScores = scoring.reference_scores ?? {};
  const soiBody = ic.soi_body ?? j.start_soi_body ?? frameToSOIBody(state.frame);

  return {
    id: j.id ?? "",
    title: j.title ?? "",
    description: j.description ?? "",
    difficulty: (j.difficulty as Difficulty) ?? Difficulty.Beginner,
    category: (j.category as CruxCategory) ?? CruxCategory.SingleManeuver,
    prerequisites: j.prerequisites ?? [],

    startTimeJD: parseStartTime(ic.start_time ?? ic.start_time_jd ?? j.start_time_jd),
    bodies: ic.bodies ?? j.bodies ?? [],
    startSOIBody: soiBody,
    startPosition: new Vector3d(pos.x ?? 0, pos.y ?? 0, pos.z ?? 0),
    startVelocity: new Vector3d(vel.x ?? 0, vel.y ?? 0, vel.z ?? 0),

    spacecraftName: sc.name ?? j.spacecraft_name ?? "Spacecraft",
    spacecraftDryMass: (sc.mass ?? 1000) - (sc.fuel_mass ?? 500),
    spacecraftFuelMass: sc.fuel_mass ?? 500,
    spacecraftISP: sc.isp ?? 300,
    spacecraftMaxThrust: sc.max_thrust ?? 10000,

    victoryConditions: parseConditions(j.victory_conditions ?? [], true, soiBody),
    failureConditions: parseConditions(j.failure_conditions ?? [], false, soiBody),

    scoring: {
      referenceOptimalDeltaV: refScores.optimal ?? 0,
      referenceGoodDeltaV: refScores.good ?? 0,
      referenceAcceptableDeltaV: refScores.acceptable ?? 0,
      referenceOptimalTime: refScores.optimal_time ?? 0,
      referenceGoodTime: refScores.good_time ?? 0,
      deltaVWeight: scoring.weights?.delta_v ?? 0.5,
      timeWeight: scoring.weights?.time ?? 0.3,
      accuracyWeight: scoring.weights?.accuracy ?? 0.2,
    },

    hints: (j.hints ?? []).map((h: any) => {
      // Hints can be plain strings or objects
      if (typeof h === "string") {
        return { hintLevel: HintLevel.Nudge, text: h, unlockAfterSeconds: 0 };
      }
      return {
        hintLevel: (h.hint_level ?? h.hintLevel) as HintLevel ?? HintLevel.Nudge,
        text: h.text ?? "",
        unlockAfterSeconds: h.unlock_after_seconds ?? h.unlockAfterSeconds ?? 0,
      };
    }),
  };
}
